"use client";

import { useActionState, useState, useEffect, useRef } from "react";
import { submitEmail } from "./actions/submit-email";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Link from "next/link";
import Turnstile from "@/components/turnstile";

const siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

export default function HomePage() {
  const [state, action, isPending] = useActionState(
    async (
      prevState: { success: boolean; message: string } | null,
      formData: FormData,
    ) => {
      return await submitEmail(formData);
    },
    null,
  );

  const [turnstileToken, setTurnstileToken] = useState<string>("");
  // const [turnstileValid, setTurnstileValid] = useState(false);
  const [turnstileChecking, setTurnstileChecking] = useState(false);
  const turnstileRef = useRef<HTMLInputElement>(null);



  useEffect(() => {
    if (turnstileRef.current) {
      turnstileRef.current.value = turnstileToken;
    }
  }, [turnstileToken]);

  const handleTurnstileVerify = (token: string) => {
    console.log(`handle verify token: ${token}`);
    setTurnstileToken(token);
    // verifyTurnstileClient(token);
     // console.log(`turnstileValid: ${turnstileValid}`);
  };

  const handleTurnstileError = () => {
    console.log(`handle error`);
    setTurnstileToken("");
    // setTurnstileValid(false);
  };

  useEffect(() => {
    console.log(`siteKey: ${siteKey}`);
  }, [siteKey]);

  console.log("siteKey type:", typeof siteKey, siteKey);
  console.log("state", state);
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Find the Best Chrome Extensions. <span className="text-blue-600">Fast.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              We're building a smarter way to discover and rank Chrome extensions — no ads, no clutter.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-2xl mx-auto">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Get notified when we launch</h2>
            <form action={action} method="POST" className="space-y-6">
              <input
                ref={turnstileRef}
                type="hidden"
                name="cf-turnstile-response"
              />
              <div className="flex flex-col sm:flex-row gap-4">
                <Input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  required
                  className="flex-1 h-12 text-lg px-4 border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
                  disabled={isPending}
                />
                <Button
                  type="submit"
                  disabled={!turnstileToken || isPending}
                  className="h-12 px-8 text-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  {isPending ? "Submitting..." : turnstileChecking ? "Verifying..." : "Notify Me"}
                </Button>
              </div>
              {siteKey && (
                <div className="mt-4">
                  <Turnstile
                    siteKey={siteKey}
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileError}
                  />
                </div>
              )}
            </form>

            {state && (
              <div
                className={`mt-6 p-4 rounded-lg text-center font-medium ${
                  state.success
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {state.message}
              </div>
            )}

            <p className="text-sm text-gray-500 mt-6">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Search</h3>
              <p className="text-gray-600">Find extensions by functionality, not just keywords</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verified Reviews</h3>
              <p className="text-gray-600">Real user feedback without fake reviews</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lightning Fast</h3>
              <p className="text-gray-600">Instant results with advanced filtering</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-600 mb-4 md:mb-0">
              © {new Date().getFullYear()} Findext. All rights reserved.
            </div>
            <nav className="flex space-x-6">
              <Link href="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
              <Link href="/privacy" className="text-gray-600 hover:text-gray-900 transition-colors">Privacy</Link>
              <Link href="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
